import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle } from "@phosphor-icons/react";

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2">
    {["Yes", "No"].map((v) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`rounded-full border px-5 py-1.5 text-xs font-semibold transition-colors ${value === v ? "border-primary bg-primary text-white" : "border-stone-200 bg-white text-stone-500 hover:border-primary/40"}`}
        data-testid={`${testid}-${v.toLowerCase()}`}>{v}</button>
    ))}
  </div>
);

export default function PublicReferencePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    relationship: "", years_known: "", kind_character: "", recommend_vulnerable: "",
    not_fit_reason: "", positive_reference: "", most_important: "", signed_name: "", family_attest: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setYN = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/public/reference/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [token]);

  const valid = form.relationship.trim() && form.kind_character && form.recommend_vulnerable
    && form.positive_reference && form.signed_name.trim() && form.family_attest;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/public/reference/${token}/submit`, form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-xl px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-12 w-auto" />
          <div>
            <p className="font-heading text-xl font-bold tracking-tight text-stone-900">Silver Home Care</p>
            <p className="text-xs text-stone-500">Reference check</p>
          </div>
        </div>

        {err && <p className="rounded-xl border border-[#C0392B]/30 bg-white p-8 text-center text-sm text-[#C0392B]" data-testid="reference-error">{err}</p>}

        {done ? (
          <div className="rounded-xl border border-stone-200 bg-white p-10 text-center shadow-sm" data-testid="reference-success">
            <CheckCircle size={44} weight="fill" className="mx-auto text-primary" />
            <h1 className="mt-4 font-heading text-2xl font-bold text-stone-900">Thank you</h1>
            <p className="mx-auto mt-3 max-w-sm text-sm text-stone-600">Your reference has been submitted to Silver Home Care. We appreciate your time.</p>
          </div>
        ) : info && !err && (
          <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8" data-testid="reference-form">
            <h1 className="font-heading text-2xl font-bold text-stone-900">Reference for {info.applicant_name}</h1>
            <p className="mt-1 text-sm leading-relaxed text-stone-500">
              {info.applicant_name} has applied for a job as a <b>Direct Care Worker</b> with Silver Home Care and listed
              you as a personal reference. Your honest answers help us keep vulnerable clients safe — this takes about two minutes.
            </p>

            <div className="mt-6 space-y-5">
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="ref-family-attest">
                <input type="checkbox" checked={form.family_attest} onChange={(e) => setForm((f) => ({ ...f, family_attest: e.target.checked }))} className="mt-0.5" />
                <span className="text-xs leading-relaxed text-stone-700">
                  I attest that I am <span className="font-semibold">not related to and not a family member of</span> {info.applicant_name}.
                </span>
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">How do you know the applicant? <span className="text-primary">*</span></label>
                  <Input value={form.relationship} onChange={set("relationship")} placeholder="e.g. neighbor, friend, former coworker" className="border-stone-300 bg-white" data-testid="ref-relationship" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-stone-500">How long have you known the applicant? (optional)</label>
                  <Input value={form.years_known} onChange={set("years_known")} placeholder="e.g. 3 years" className="border-stone-300 bg-white" data-testid="ref-years" />
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-stone-700">Do you know the applicant to be kind and of good character? <span className="text-primary">*</span></p>
                <YesNo value={form.kind_character} onChange={setYN("kind_character")} testid="ref-kind-character" />
              </div>

              <div>
                <p className="mb-2 text-xs font-medium leading-relaxed text-stone-700">Would you recommend the applicant to care for vulnerable individuals in their own homes, under minimal supervision? <span className="text-primary">*</span></p>
                <YesNo value={form.recommend_vulnerable} onChange={setYN("recommend_vulnerable")} testid="ref-recommend" />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Is there any reason you can think of that they may not be a good fit for this position?</label>
                <Textarea rows={2} value={form.not_fit_reason} onChange={set("not_fit_reason")} className="border-stone-300 bg-white" data-testid="ref-not-fit" />
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-stone-700">Is this a positive reference? <span className="text-primary">*</span></p>
                <YesNo value={form.positive_reference} onChange={setYN("positive_reference")} testid="ref-positive" />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">What is the most important thing we should know about them?</label>
                <Textarea value={form.most_important} onChange={set("most_important")} placeholder="The one thing that would help us most — good or bad." className="min-h-16 border-stone-300 bg-white text-sm" data-testid="ref-most-important" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-stone-500">Type your full name to sign <span className="text-primary">*</span></label>
                <Input value={form.signed_name} onChange={set("signed_name")} className="border-stone-300 bg-white" data-testid="ref-sign" />
              </div>
              <Button onClick={submit} disabled={busy || !valid}
                className="w-full rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-stone-900 active:scale-95" data-testid="ref-submit">
                {busy ? "Submitting…" : "Submit reference"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
